// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, Button, Dialog, Tabs } from '@node-space/ui-components'
import { OnceOffTransactionExportForm } from 'components/export/onceOff/OnceOffTransactionExportForm'
import { RecurringTransactionsExport } from 'components/export/recurring/RecurringTransactionsExport'
import { OnceOffExportContextProvider } from 'contexts/onceOffExportContext'
import { RecurringExportContextProvider } from 'contexts/recurringExportContext'
import { useWalletContext } from 'hooks/context'
import { useReportScheduleQuery } from 'hooks/queries/useReportScheduleQuery'
import { useReportScheduleTimezonesQuery } from 'hooks/queries/useReportScheduleTimezonesQuery'
import { useOnceOffWalletTransactionsExport } from 'hooks/transactionsExport/onceOff/useOnceOffWalletTransactionsExport'
import { useRecurringTransactionsExport } from 'hooks/transactionsExport/recurring/useRecurringTransactionsExport'
import { CustomerWallet } from 'types/customers'
import {
  ExportTabItems,
  ExportTabItemsType,
  RecurringExportForm,
  RecurringExportSteps,
} from 'types/transactionsExport'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

interface WalletTransactionExportModalProps {
  handleClose: () => void
  downloadAll: boolean
  customerWallet?: CustomerWallet
}

export const WalletTransactionExportModal = ({
  handleClose,
  downloadAll,
  customerWallet,
}: WalletTransactionExportModalProps) => {
  const { t } = useTranslation()
  const [openTab, setOpenTab] = useState<ExportTabItemsType>(ExportTabItems.ONCE_OFF)
  const wallet = useWalletContext()
  const walletId = customerWallet?.id ?? wallet?.lsid

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.EXPORT_TRANSACTION_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.LOAD,
    })
  }, [])

  const onceOffValues = useOnceOffWalletTransactionsExport({
    downloadAll,
    handleClose,
    customerWallet,
  })
  const recurringValues = useRecurringTransactionsExport({
    handleClose,
    setOpenTab,
    walletId,
  })

  const { control } = recurringValues.form

  const recurringformValues = useWatch({ control }) as RecurringExportForm

  const { enableRecurringExports } = useFeatureFlags()

  const { isFetching: isFetchingTimezones } = useReportScheduleTimezonesQuery(
    openTab === ExportTabItems.RECURRING && recurringValues?.step === RecurringExportSteps.FORM
  )

  const {
    data: reportSchedules = [],
    isFetching,
    isError,
  } = useReportScheduleQuery(walletId, openTab === ExportTabItems.RECURRING)

  const isLoadingCreateSchedule =
    isFetchingTimezones || recurringValues?.isLoadingPostReportSchedule
  const isLoadingUpdateSchedule = isFetchingTimezones || recurringValues?.isLoadingPutReportSchedule

  const CancelButton = (
    <Button key="cancel" onClick={handleClose} variant="secondary">
      {t('exports.cancel')}
    </Button>
  )

  const getPrimaryAction = () => {
    if (openTab === ExportTabItems.ONCE_OFF || !enableRecurringExports) {
      return (
        <Button
          key="exportToEmail"
          onClick={onceOffValues?.handleTransactionExportToEmail}
          loading={onceOffValues?.isExporting}
          disabled={
            (onceOffValues?.formValues?.selectedDateOptionName ==
              onceOffValues?.exportCustomDateOptions?.customDateRange?.name &&
              !onceOffValues?.dateValue?.from &&
              !onceOffValues?.dateValue?.to) ||
            (onceOffValues?.formValues?.reportType === 'BALANCE' &&
              !onceOffValues?.formValues?.balanceDate)
          }
        >
          {t('exports.exportToEmail')}
        </Button>
      )
    }
    if (openTab === ExportTabItems.RECURRING && !isFetching && !isError) {
      if (recurringValues?.step === RecurringExportSteps.LIST) {
        return (
          <Button
            key="newSchedule"
            onClick={() => {
              recurringValues?.setIsUpdatingReportSchedule(false)
              recurringValues?.setCurrentReportSchedule(null)
              recurringValues?.form.reset()
              recurringValues?.setStep(RecurringExportSteps.FORM)
            }}
          >
            {t('exports.newSchedule')}
          </Button>
        )
      }
      return recurringValues?.isUpdatingReportSchedule ? (
        <Button
          key="updateSchedule"
          onClick={() => recurringValues?.updateReportSchedule(recurringformValues)}
          disabled={isLoadingUpdateSchedule}
          loading={isLoadingUpdateSchedule}
        >
          {t('exports.updateSchedule')}
        </Button>
      ) : (
        <Button
          key="createSchedule"
          onClick={() => recurringValues?.createReportSchedule(recurringformValues)}
          disabled={isLoadingCreateSchedule}
          loading={isLoadingCreateSchedule}
        >
          {t('exports.createSchedule')}
        </Button>
      )
    }
  }

  const handleTabChange = (name: string) => {
    setOpenTab(name as ExportTabItemsType)
    recurringValues?.setStep(RecurringExportSteps.LIST)
    recurringValues?.setCurrentReportSchedule(null)
    recurringValues?.setIsUpdatingReportSchedule(false)
  }

  return (
    <Dialog
      title={t('exports.modalTitle')}
      type="utility"
      open
      onClose={handleClose}
      ActionButtons={[getPrimaryAction(), CancelButton]}
    >
      {enableRecurringExports ? (
        <Box className="min-h-[370px]">
          <Tabs
            defaultTabId={ExportTabItems.ONCE_OFF}
            onChange={handleTabChange}
            tabs={[
              {
                header: t('exports.onceOff'),
                id: ExportTabItems.ONCE_OFF,
                testId: ExportTabItems.ONCE_OFF,
                content: (
                  <OnceOffExportContextProvider value={onceOffValues}>
                    <OnceOffTransactionExportForm
                      // When downloadAll always uses CSV format
                      showFormatSelector={!downloadAll}
                    />
                  </OnceOffExportContextProvider>
                ),
              },
              {
                header: t('exports.recurring'),
                id: ExportTabItems.RECURRING,
                testId: ExportTabItems.RECURRING,
                content: (
                  <RecurringExportContextProvider value={recurringValues}>
                    <RecurringTransactionsExport downloadAll={downloadAll} />
                  </RecurringExportContextProvider>
                ),
              },
            ]}
          />
        </Box>
      ) : (
        <OnceOffExportContextProvider value={onceOffValues}>
          <OnceOffTransactionExportForm
            // When downloadAll always uses CSV format
            showFormatSelector={!downloadAll}
          />
        </OnceOffExportContextProvider>
      )}
    </Dialog>
  )
}
